import type { ReactNode, RefObject } from 'react'
import { Braces, Copy, Download, Ellipsis, Footprints, LineChart, Search, Settings, X } from 'lucide-react'
import type { Chart } from 'klinecharts'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/useIsMobile'
// Relative imports (not the '@/components' barrel) to avoid a cycle now that
// the barrel exports Workbench, which renders this file.
import { Checkbox } from '../Checkbox/Checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../Tooltip/Tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../DropdownMenu/DropdownMenu'
import { WorkbenchTimeframe } from './WorkbenchTimeframe'
import { WorkbenchChartType } from './WorkbenchChartType'
import { WorkbenchCamera } from './WorkbenchCamera'
import { useWorkbenchSnapshot } from './useWorkbenchSnapshot'
import { CHART_TYPES, TIMEFRAME_LABELS, TIMEFRAME_OPTIONS } from './catalog'
import type { ChartTypeId } from './types'

// Top toolbar for the Strategy Design chart. Desktop lays out the chart
// controls in TradingView order — symbol search · timeframe · chart-type ·
// indicators on the left, utility actions (camera, settings, show-state) on
// the right. Mobile keeps only symbol · backtest-trades visible and folds
// everything else into an overflow menu.

export function ToolbarDivider() {
  return <div className="w-px h-5 bg-border mx-0.5 shrink-0" />
}

export interface WorkbenchTopToolbarProps {
  chart: Chart | null
  captureRef: RefObject<HTMLDivElement | null>
  symbol: string
  onOpenSymbolSearch: () => void
  timeframe: string
  onTimeframeChange: (tf: string) => void
  chartType: ChartTypeId
  onChartTypeChange: (id: ChartTypeId) => void
  onOpenIndicators: () => void
  backtestOrdersOn?: boolean
  backtestOrdersDisabled?: boolean
  backtestOrdersTitle?: string
  onToggleBacktestOrders?: () => void
  onOpenSettings: () => void
  onShowState: () => void
  /** Start a walkthrough of the generated code over the period the user drew on. */
  onEnterWalkthrough?: () => void
  /**
   * Why the walkthrough is unavailable. Disables the button and explains it on hover.
   *
   * Deliberately a message rather than a boolean: "you can't do this" without "here's what
   * to do first" is the worst version of a disabled control, and every blocked case here has
   * a concrete next step (draw something, generate the code).
   */
  walkthroughDisabledReason?: string | null
  /**
   * Which walkthrough state this spot should report, or null when the mode is off.
   *
   * The mode is entered long before it can be watched — the run has to be built first — and
   * the toolbar is locked for all of it. A green "running" through the wait would be a lie
   * about the one thing the user is waiting on, so the preparing phase says so itself.
   */
  walkthroughStatus?: WalkthroughStatusKind | null
  /**
   * Leave the mode.
   *
   * Here rather than on the transport bar because the way out belongs where the way in was:
   * one spot answers "am I in a walkthrough?" and "get me out of it" both.
   */
  onExitWalkthrough?: () => void
  /**
   * Read-only mode (Walkthrough).
   *
   * Symbol / timeframe / chart type become static labels rather than controls: the run is
   * pinned to the symbol and timeframe it was resolved for, so changing them would silently
   * describe a different chart than the one being replayed. The editing affordances
   * (indicators, backtest overlay, camera, settings) are hidden outright — there is
   * nothing for them to act on.
   */
  locked?: boolean
}

export function WorkbenchTopToolbar(props: WorkbenchTopToolbarProps) {
  const isMobile = useIsMobile()
  return isMobile ? <MobileToolbar {...props} /> : <DesktopToolbar {...props} />
}

function ToolbarShell({ children }: { children: ReactNode }) {
  return <div className="shrink-0 h-10 flex items-center gap-1 px-2 border-b border-border bg-background">{children}</div>
}

function SymbolButton({
  symbol,
  onOpenSymbolSearch,
  locked = false,
}: Pick<WorkbenchTopToolbarProps, 'symbol' | 'onOpenSymbolSearch' | 'locked'>) {
  if (locked) {
    return (
      <span
        id="symbol-search-btn"
        className="inline-flex items-center gap-1.5 h-7 pl-2 pr-2.5 rounded-md shrink-0 select-none"
      >
        <span className="text-label font-semibold text-foreground tracking-tight">{symbol}</span>
      </span>
    )
  }
  return (
    <button
      id="symbol-search-btn"
      type="button"
      onClick={onOpenSymbolSearch}
      className="inline-flex items-center gap-1.5 h-7 pl-2 pr-2.5 rounded-md hover:bg-muted/60 transition-colors cursor-pointer shrink-0"
    >
      <Search size={14} strokeWidth={1.75} className="text-muted-foreground" />
      <span className="text-label font-semibold text-foreground tracking-tight">{symbol}</span>
    </button>
  )
}

/* Backtest-orders overlay checkbox: checked = the last backtest's trades are
   drawn on the chart. Disabled (with the reason in the label's `title`) when
   there's no finished backtest or its symbol differs from the chart. */
function BacktestToggle({
  label,
  backtestOrdersOn = false,
  backtestOrdersDisabled = true,
  backtestOrdersTitle,
  onToggleBacktestOrders,
}: Pick<
  WorkbenchTopToolbarProps,
  'backtestOrdersOn' | 'backtestOrdersDisabled' | 'backtestOrdersTitle' | 'onToggleBacktestOrders'
> & { label: string }) {
  return (
    <label
      htmlFor="backtest-orders-btn"
      title={backtestOrdersTitle}
      className={cn(
        'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-label transition-colors shrink-0 select-none',
        backtestOrdersDisabled
          ? 'text-text-disabled cursor-not-allowed'
          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground cursor-pointer',
      )}
    >
      <Checkbox
        id="backtest-orders-btn"
        checked={backtestOrdersOn}
        disabled={backtestOrdersDisabled}
        onCheckedChange={() => onToggleBacktestOrders?.()}
        className="size-3.5"
      />
      {label}
    </label>
  )
}

/**
 * The way INTO walkthrough mode, and the only one.
 *
 * "Test" is doing real work in that name: it says the run is a rehearsal of the trader's own
 * trades rather than a live thing, which is the one question an unlabelled footprints glyph
 * leaves open. While the mode is on this button is replaced IN PLACE by WalkthroughStatus,
 * which carries the way out — so the switch is one spot, not two. Canvas is still not a mode
 * you switch INTO: it is simply where you are when no walkthrough is running.
 */
function WalkthroughButton({
  onEnterWalkthrough,
  walkthroughDisabledReason = null,
  compact = false,
}: Pick<WorkbenchTopToolbarProps, 'onEnterWalkthrough' | 'walkthroughDisabledReason'> & {
  /** Icon only. The label is already `hidden sm:inline`; this drops it at every width. */
  compact?: boolean
}) {
  const blocked = !!walkthroughDisabledReason
  const button = (
    <button
      type="button"
      onClick={() => {
        if (!blocked) onEnterWalkthrough?.()
      }}
      disabled={blocked}
      aria-label="Walkthrough Test"
      className={cn(
        'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-label transition-colors shrink-0',
        blocked
          ? 'text-muted-foreground opacity-50 cursor-not-allowed'
          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground cursor-pointer',
      )}
    >
      <Footprints size={14} strokeWidth={1.75} className="shrink-0" />
      {!compact && <span className="hidden sm:inline">Walkthrough Test</span>}
    </button>
  )

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        {/* A disabled button fires no pointer events, so the trigger wraps it. */}
        <TooltipTrigger asChild>
          <span className="inline-flex">{button}</span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[15rem]">
          {walkthroughDisabledReason ?? 'Walk through your trades'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export type WalkthroughStatusKind = 'preparing' | 'running' | 'failed'

/** Dot colour and words per state. The dot pulses only while something is still happening. */
const WALKTHROUGH_STATUS: Record<WalkthroughStatusKind, { label: string; dot: string }> = {
  preparing: { label: 'Preparing…', dot: 'bg-warning animate-pulse' },
  running: { label: 'Walkthrough running', dot: 'bg-success' },
  failed: { label: 'Walkthrough failed', dot: 'bg-danger' },
}

/**
 * What the Walkthrough Test button becomes while the mode is on: the state, and the way out.
 *
 * It occupies the button's own spot deliberately. Entering from the toolbar and leaving from
 * the transport bar at the far end of the chart made leaving a thing you had to go and find,
 * and left the toolbar with nothing to say about a mode that had taken over the whole surface.
 *
 * A status, not a toggle. Clicking "Walkthrough running" to stop it would read as a switch the
 * label does not describe, so the exit is its own button and says Exit.
 */
function WalkthroughStatus({
  status,
  onExitWalkthrough,
  compact = false,
}: {
  status: WalkthroughStatusKind
  onExitWalkthrough?: () => void
  /** Dot plus glyph, no words — the mobile toolbar has no room for the sentence. */
  compact?: boolean
}) {
  const { label, dot } = WALKTHROUGH_STATUS[status]
  return (
    <>
      <span
        role="status"
        // BOTH, and they are not the same job. `status` is not a name-from-content role, so
        // without the label the region has no accessible name at all; and a label is not what
        // a polite region announces, so without the text compact — whose dot and glyph are
        // both decorative — would reach "running" with nothing to say. sr-only keeps that
        // announcement and costs the row no width.
        aria-label={label}
        className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-label text-muted-foreground select-none shrink-0"
      >
        <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full shrink-0', dot)} />
        {compact && <Footprints size={14} strokeWidth={1.75} aria-hidden className="shrink-0" />}
        <span className={compact ? 'sr-only' : undefined}>{label}</span>
      </span>
      {!compact && <ToolbarDivider />}
      <button
        type="button"
        onClick={onExitWalkthrough}
        title="Leave the walkthrough"
        aria-label="Exit walkthrough"
        className={cn(
          'inline-flex items-center justify-center gap-1.5 h-7 rounded-md text-label transition-colors cursor-pointer shrink-0',
          'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          compact ? 'w-7' : 'px-2.5',
        )}
      >
        <X size={14} strokeWidth={2} className="shrink-0" />
        {!compact && <span>Exit</span>}
      </button>
    </>
  )
}

function DesktopToolbar(props: WorkbenchTopToolbarProps) {
  const { chart, captureRef, timeframe, onTimeframeChange, chartType, onChartTypeChange, onOpenIndicators, onOpenSettings, onShowState, locked = false, walkthroughStatus, onExitWalkthrough } = props
  if (locked) {
    return (
      <ToolbarShell>
        <SymbolButton {...props} />
        <ToolbarDivider />
        <span className="text-label tabular-nums text-muted-foreground select-none px-1">
          {(TIMEFRAME_LABELS as Record<string, string>)[timeframe] ?? timeframe}
        </span>
        {/* Same ml-auto group the Walkthrough Test button sits in below, so the state and the
            way out land exactly where the way in was. */}
        {walkthroughStatus && (
          <div className="ml-auto flex items-center gap-1">
            <WalkthroughStatus
              status={walkthroughStatus}
              onExitWalkthrough={onExitWalkthrough}
            />
          </div>
        )}
      </ToolbarShell>
    )
  }
  return (
    <ToolbarShell>
      {/* left group: market + chart-shape controls */}
      <SymbolButton {...props} />
      <ToolbarDivider />
      <WorkbenchTimeframe value={timeframe} onChange={onTimeframeChange} />
      <ToolbarDivider />
      <WorkbenchChartType value={chartType} onChange={onChartTypeChange} />
      <ToolbarDivider />
      <button
        id="indicators-btn"
        type="button"
        onClick={onOpenIndicators}
        className={cn(
          'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-label transition-colors cursor-pointer',
          'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )}
      >
        <LineChart size={14} strokeWidth={1.75} />
        Indicators
      </button>
      <BacktestToggle label="Overlay backtest" {...props} />

      {/* right group: utility actions */}
      <div className="ml-auto flex items-center gap-1">
        <WalkthroughButton {...props} />
        <ToolbarDivider />
        <WorkbenchCamera chart={chart} captureRef={captureRef} />
        <button
          type="button"
          onClick={onOpenSettings}
          aria-label="Chart settings"
          className={cn(
            'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors cursor-pointer',
            'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          )}
        >
          <Settings size={15} strokeWidth={1.75} />
        </button>
        {/* Dev-only aid: ships hidden. Re-enable from devtools with
            `display: inline-flex` on [data-debug="show-state"]. */}
        <button
          type="button"
          onClick={onShowState}
          aria-label="Show State"
          data-debug="show-state"
          className={cn(
            'hidden items-center gap-1.5 h-7 px-2.5 rounded-md text-label transition-colors cursor-pointer',
            'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          )}
        >
          <Braces size={14} strokeWidth={1.75} />
          Show State
        </button>
      </div>
    </ToolbarShell>
  )
}

function MobileToolbar(props: WorkbenchTopToolbarProps) {
  const { chart, captureRef, timeframe, onTimeframeChange, chartType, onChartTypeChange, onOpenIndicators, onOpenSettings, onShowState, locked = false, walkthroughStatus, onExitWalkthrough } = props
  const snapshot = useWorkbenchSnapshot(chart, captureRef)
  const tfLabel = (TIMEFRAME_LABELS as Record<string, string>)[timeframe] ?? timeframe
  if (locked) {
    return (
      <ToolbarShell>
        <SymbolButton {...props} />
        <span className="text-label tabular-nums text-muted-foreground select-none px-1">{tfLabel}</span>
        {walkthroughStatus && (
          <div className="ml-auto flex items-center gap-1">
            <WalkthroughStatus
              status={walkthroughStatus}
              onExitWalkthrough={onExitWalkthrough}
              compact
            />
          </div>
        )}
      </ToolbarShell>
    )
  }
  return (
    <ToolbarShell>
      <SymbolButton {...props} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="More tools"
            className={cn(
              'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors cursor-pointer shrink-0',
              'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
            )}
          >
            <Ellipsis size={16} strokeWidth={1.75} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <span className="flex-1">Timeframe</span>
              <span className="text-muted-foreground tabular-nums mr-1">{tfLabel}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={timeframe} onValueChange={onTimeframeChange}>
                {TIMEFRAME_OPTIONS.map((tf) => (
                  <DropdownMenuRadioItem key={tf} value={tf} className="tabular-nums">
                    {TIMEFRAME_LABELS[tf]}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Chart type</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={chartType} onValueChange={(v) => onChartTypeChange(v as ChartTypeId)}>
                {CHART_TYPES.map((t) => (
                  <DropdownMenuRadioItem key={t.id} value={t.id}>
                    {t.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => onOpenIndicators()}>
            <LineChart size={14} strokeWidth={1.75} />
            Indicators…
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void snapshot.save()}>
            <Download size={14} strokeWidth={1.75} />
            Save chart image
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void snapshot.copy()}>
            <Copy size={14} strokeWidth={1.75} />
            Copy chart image
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onOpenSettings()}>
            <Settings size={14} strokeWidth={1.75} />
            Chart settings…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <BacktestToggle label="Backtest" {...props} />
      <div className="ml-auto flex items-center gap-1">
        {/* Icon only here: at this width the row already carries the symbol, the overflow
            menu and the backtest checkbox, and the label would push one of them off. */}
        <WalkthroughButton {...props} compact />
        {/* Dev-only aid, ships hidden (same devtools re-enable as desktop). */}
        <button
          type="button"
          onClick={onShowState}
          aria-label="Show State"
          data-debug="show-state"
          className="hidden items-center gap-1.5 h-7 px-2 rounded-md text-label text-muted-foreground cursor-pointer"
        >
          <Braces size={14} strokeWidth={1.75} />
        </button>
      </div>
    </ToolbarShell>
  )
}
